import React, { useState, useMemo } from 'react';
import { Trophy, Star, Gift, Users, Settings, Crown, Plus, Edit, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { mockUsers } from '@/data/mock';
import { toast } from 'sonner';

const TIER_COLORS = {
  'Starter': '#94a3b8',
  'Trader': '#3b82f6',
  'Pro Trader': '#8b5cf6',
  'Elite Trader': '#f59e0b',
};

export const LoyaltyProgramPage = () => {
  // Points Rules State
  const [pointsRules, setPointsRules] = useState([
    { id: 1, action: 'Sign Up', points: 100, limit: 1, limitType: 'times', enabled: true },
    { id: 2, action: 'Submit Review', points: 50, limit: 5, limitType: 'times', enabled: true },
    { id: 3, action: 'Review Gets 10 Helpful Votes', points: 25, limit: null, limitType: 'unlimited', enabled: true },
    { id: 4, action: 'Daily Login', points: 5, limit: 1, limitType: 'day', enabled: true },
    { id: 5, action: 'Complete Profile', points: 75, limit: 1, limitType: 'times', enabled: true },
    { id: 6, action: 'Refer a Friend', points: 200, limit: null, limitType: 'unlimited', enabled: true },
  ]);
  const [editingRule, setEditingRule] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);

  // Tiers State
  const [tiers, setTiers] = useState([
    { id: 1, name: 'Starter', threshold: 0, pointsRequired: 0, invitesRequired: 0, perks: 'Access to reviews\nBasic badges' },
    { id: 2, name: 'Trader', threshold: 500, pointsRequired: 500, invitesRequired: 2, perks: 'Priority support\nFeatured reviews\nDiscount codes' },
    { id: 3, name: 'Pro Trader', threshold: 2000, pointsRequired: 2000, invitesRequired: 5, perks: 'All Trader perks\nVerified badge\nEarly access' },
    { id: 4, name: 'Elite Trader', threshold: 5000, pointsRequired: 5000, invitesRequired: 10, perks: 'All Pro perks\nExclusive events\nPremium support' },
  ]);
  const [editingTier, setEditingTier] = useState(null);

  // Rewards State
  const [rewards, setRewards] = useState([
    { id: 1, title: '10% OFF Coupon', pointsCost: 500, description: 'Get 10% discount on any prop firm challenge', active: true },
    { id: 2, title: 'Premium Badge', pointsCost: 1000, description: 'Get a verified premium badge on your profile', active: true },
    { id: 3, title: 'Featured Review', pointsCost: 750, description: 'Your next review will be featured on homepage for 7 days', active: true },
  ]);
  const [editingReward, setEditingReward] = useState(null);
  const [deleteReward, setDeleteReward] = useState(null);

  const totalPointsIssued = useMemo(() => 
    mockUsers.reduce((sum, user) => sum + user.points, 0),
    [mockUsers]
  );
  
  const activeMembers = useMemo(() => 
    mockUsers.filter(u => u.status === 'active').length,
    [mockUsers]
  );
  
  const avgPoints = useMemo(() => 
    Math.round(totalPointsIssued / mockUsers.length),
    [totalPointsIssued, mockUsers.length]
  );

  const tierDistribution = useMemo(() => [
    { name: 'Starter', value: mockUsers.filter(u => u.tier === 'Starter').length },
    { name: 'Trader', value: mockUsers.filter(u => u.tier === 'Trader').length },
    { name: 'Pro Trader', value: mockUsers.filter(u => u.tier === 'Pro Trader').length },
    { name: 'Elite Trader', value: mockUsers.filter(u => u.tier === 'Elite Trader').length },
  ], [mockUsers]);

  // Rule CRUD
  const handleAddRule = () => {
    setEditingRule({ id: Date.now(), action: '', points: 0, limit: 1, limitType: 'times', enabled: true });
  };

  const handleSaveRule = () => {
    if (!editingRule.action || !editingRule.points) {
      toast.error('Please fill in action and points');
      return;
    }
    if (editingRule.id > 100) {
      setPointsRules([...pointsRules, editingRule]);
      toast.success('Rule added');
    } else {
      setPointsRules(pointsRules.map(r => r.id === editingRule.id ? editingRule : r));
      toast.success('Rule updated');
    }
    setEditingRule(null);
  };

  const handleDeleteRule = () => {
    setPointsRules(pointsRules.filter(r => r.id !== deleteRule.id));
    toast.success('Rule deleted');
    setDeleteRule(null);
  };

  // Tier CRUD
  const handleSaveTier = () => {
    if (!editingTier.name || !editingTier.pointsRequired) {
      toast.error('Please fill in tier name and points required');
      return;
    }
    setTiers(tiers.map(t => t.id === editingTier.id ? editingTier : t));
    toast.success('Tier updated');
    setEditingTier(null);
  };

  // Reward CRUD
  const handleAddReward = () => {
    setEditingReward({ id: Date.now(), title: '', pointsCost: 0, description: '', active: true });
  };

  const handleSaveReward = () => {
    if (!editingReward.title || !editingReward.pointsCost) {
      toast.error('Please fill in title and points cost');
      return;
    }
    if (editingReward.id > 100) {
      setRewards([...rewards, editingReward]);
      toast.success('Reward added');
    } else {
      setRewards(rewards.map(r => r.id === editingReward.id ? editingReward : r));
      toast.success('Reward updated');
    }
    setEditingReward(null);
  };

  const handleDeleteReward = () => {
    setRewards(rewards.filter(r => r.id !== deleteReward.id));
    toast.success('Reward deleted');
    setDeleteReward(null);
  };

  return (
    <div>
      <PageHeader
        title="Loyalty Program"
        description="Manage points, tiers, and rewards system"
        breadcrumbs={['Admin', 'Community', 'Loyalty Program']}
      />

      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <StatsCard
          title="Total Points Issued"
          value={totalPointsIssued.toLocaleString()}
          icon={Star}
          trend={{ value: '+12%', positive: true }}
        />
        <StatsCard
          title="Active Members"
          value={activeMembers}
          icon={Users}
          trend={{ value: '+5', positive: true }}
        />
        <StatsCard
          title="Avg Points"
          value={avgPoints}
          icon={Trophy}
        />
        <StatsCard
          title="Active Rewards"
          value={rewards.filter(r => r.active).length}
          icon={Gift}
        />
      </div>

      <Tabs defaultValue="rules" className="space-y-6">
        <TabsList>
          <TabsTrigger value="rules">
            <Settings className="h-4 w-4 mr-2" />
            Points Rules
          </TabsTrigger>
          <TabsTrigger value="tiers">
            <Crown className="h-4 w-4 mr-2" />
            Tiers
          </TabsTrigger>
          <TabsTrigger value="rewards">
            <Gift className="h-4 w-4 mr-2" />
            Rewards
          </TabsTrigger>
          <TabsTrigger value="overview">
            <Trophy className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
        </TabsList>

        {/* Points Rules Tab */}
        <TabsContent value="rules">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Points Rules</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Configure how users earn points</p>
              </div>
              <Button onClick={handleAddRule}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ACTION</TableHead>
                    <TableHead>POINTS</TableHead>
                    <TableHead>LIMIT</TableHead>
                    <TableHead>STATUS</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pointsRules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">{rule.action}</TableCell>
                      <TableCell>
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          +{rule.points} pts
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rule.limitType === 'unlimited' ? (
                          <span className="text-sm text-slate-500">Unlimited</span>
                        ) : (
                          <span className="text-sm text-slate-600">
                            {rule.limit} {rule.limitType === 'day' ? '/ day' : 
                                         rule.limitType === 'month' ? '/ month' : 'times'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={(checked) => {
                            setPointsRules(pointsRules.map(r => 
                              r.id === rule.id ? { ...r, enabled: checked } : r
                            ));
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditingRule(rule)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={() => setDeleteRule(rule)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tiers Tab */}
        <TabsContent value="tiers">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Membership Tiers</CardTitle>
              <p className="text-sm text-slate-600">Configure tier levels and requirements</p>
            </CardHeader>
            <CardContent className="space-y-4">
              {tiers.map((tier) => (
                <Card key={tier.id} className="border-2" style={{ borderColor: TIER_COLORS[tier.name] }}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Crown className="h-5 w-5" style={{ color: TIER_COLORS[tier.name] }} />
                          <h3 className="text-lg font-semibold">{tier.name}</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-slate-500">Points Required</p>
                            <p className="text-sm font-medium">{tier.pointsRequired} points</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Invites Required</p>
                            <p className="text-sm font-medium">{tier.invitesRequired} members</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Perks & Rewards</p>
                          <div className="text-sm text-slate-700 whitespace-pre-line">
                            {tier.perks}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingTier(tier)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards">
          <Card className="rounded-xl border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Rewards Catalog</CardTitle>
                <p className="text-sm text-slate-600 mt-1">Manage redeemable rewards</p>
              </div>
              <Button onClick={handleAddReward}>
                <Plus className="h-4 w-4 mr-2" />
                Add Reward
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {rewards.map((reward) => (
                  <Card key={reward.id} className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <Gift className="h-8 w-8 text-blue-600" />
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setEditingReward(reward)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-600"
                            onClick={() => setDeleteReward(reward)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <h4 className="font-semibold mb-1">{reward.title}</h4>
                      <p className="text-sm text-slate-600 mb-3">{reward.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200">
                          {reward.pointsCost} points
                        </Badge>
                        {reward.active ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Tier Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={tierDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {tierDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={TIER_COLORS[entry.name]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="rounded-xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Program Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Points Expiry</p>
                    <p className="text-sm text-slate-600">Points expire after 12 months of inactivity</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-slate-600">Notify users when they earn points</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Tier Badges</p>
                    <p className="text-sm text-slate-600">Show tier badges on user profiles</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Rule Dialog */}
      <Dialog open={!!editingRule} onOpenChange={() => setEditingRule(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRule?.id > 100 ? 'Add' : 'Edit'} Points Rule</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Action Name</Label>
              <Input
                value={editingRule?.action || ''}
                onChange={(e) => setEditingRule({ ...editingRule, action: e.target.value })}
                placeholder="e.g., Submit Review"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Points Awarded</Label>
              <Input
                type="number"
                value={editingRule?.points || ''}
                onChange={(e) => setEditingRule({ ...editingRule, points: parseInt(e.target.value) || 0 })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Limit Type</Label>
              <Select
                value={editingRule?.limitType || 'times'}
                onValueChange={(value) => setEditingRule({ ...editingRule, limitType: value, limit: value === 'unlimited' ? null : 1 })}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="times">Times</SelectItem>
                  <SelectItem value="day">Per Day</SelectItem>
                  <SelectItem value="month">Per Month</SelectItem>
                  <SelectItem value="unlimited">Unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {editingRule?.limitType !== 'unlimited' && (
              <div>
                <Label>Limit</Label>
                <Input
                  type="number"
                  value={editingRule?.limit || ''}
                  onChange={(e) => setEditingRule({ ...editingRule, limit: parseInt(e.target.value) || 1 })}
                  className="mt-2"
                />
              </div>
            )}
            <Button onClick={handleSaveRule} className="w-full">
              {editingRule?.id > 100 ? 'Add' : 'Save'} Rule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tier Dialog */}
      <Dialog open={!!editingTier} onOpenChange={() => setEditingTier(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tier - {editingTier?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Points Required</Label>
              <Input
                type="number"
                value={editingTier?.pointsRequired || ''}
                onChange={(e) => setEditingTier({ ...editingTier, pointsRequired: parseInt(e.target.value) || 0 })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Invites Required</Label>
              <Input
                type="number"
                value={editingTier?.invitesRequired || ''}
                onChange={(e) => setEditingTier({ ...editingTier, invitesRequired: parseInt(e.target.value) || 0 })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Perks & Rewards</Label>
              <Textarea
                value={editingTier?.perks || ''}
                onChange={(e) => setEditingTier({ ...editingTier, perks: e.target.value })}
                rows={4}
                placeholder="Enter one perk per line"
                className="mt-2"
              />
              <p className="text-xs text-slate-500 mt-1">Each line will be displayed as a separate perk</p>
            </div>
            <Button onClick={handleSaveTier} className="w-full">
              Save Tier
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Reward Dialog */}
      <Dialog open={!!editingReward} onOpenChange={() => setEditingReward(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingReward?.id > 100 ? 'Add' : 'Edit'} Reward</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Reward Title</Label>
              <Input
                value={editingReward?.title || ''}
                onChange={(e) => setEditingReward({ ...editingReward, title: e.target.value })}
                placeholder="e.g., 10% OFF Coupon"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Points Cost</Label>
              <Input
                type="number"
                value={editingReward?.pointsCost || ''}
                onChange={(e) => setEditingReward({ ...editingReward, pointsCost: parseInt(e.target.value) || 0 })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={editingReward?.description || ''}
                onChange={(e) => setEditingReward({ ...editingReward, description: e.target.value })}
                rows={3}
                className="mt-2"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <Label>Active</Label>
              <Switch
                checked={editingReward?.active || false}
                onCheckedChange={(checked) => setEditingReward({ ...editingReward, active: checked })}
              />
            </div>
            <Button onClick={handleSaveReward} className="w-full">
              {editingReward?.id > 100 ? 'Add' : 'Save'} Reward
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmations */}
      <ConfirmDialog
        open={!!deleteRule}
        onOpenChange={() => setDeleteRule(null)}
        title="Delete Points Rule"
        description={`Are you sure you want to delete the rule "${deleteRule?.action}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteRule}
      />

      <ConfirmDialog
        open={!!deleteReward}
        onOpenChange={() => setDeleteReward(null)}
        title="Delete Reward"
        description={`Are you sure you want to delete "${deleteReward?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteReward}
      />
    </div>
  );
};
